// ─── Constants ────────────────────────────────────────────────────────────────

const CLASSES = {
  open: "is-open",
  mobileOpen: "mobile-navigation-open",
  mobileLVL2open: "mobile-navigation-lvl2-open",
  animationStart: "animation-start",
  mobileActive: "mobile-active",
  accordionOpened: "enh-nav__accordion-button--opened",
};

const SELECTORS = {
  serviceNavigation: ".enh-nav__service-navigation",
  dialog: ".enh-nav__dialog",
  dialogContent: ".enh-nav__dialog-content",
  branchLevel1: ".enh-nav__branch--level-1",
  linkContainer: ".enh-nav__link-container",
  lvl1Link: ".enh-nav__link--primary",
  lvl1ActiveLink: ".enh-nav__link--primary.active",
  lvl2and3ActiveLink:
    ".enh-nav__link--secondary.active, .enh-nav__link--tertiary.active",
  dialogLevel2: ".enh-nav__dialog-level-2",
  toggleMobileNavBtn: ".enh-nav__button--hamburger",
  backButton: ".enh-nav__button--back",
  focusableElements:
    ":where(a[href],area[href],input,select,textarea,button,iframe,object,embed,[tabindex],[contenteditable]):not([tabindex='-1'],[disabled])",
  langSelector: ".enh-nav__dialog-lang-selector .site-selector a",
  dialogLevel2Open: `.enh-nav__dialog-level-2.${CLASSES.open}`,
  search: ".js-site-search a",
  focusDropdownButton: ".enh-nav__focus-dropdown-button",
  accordionButton: ".enh-nav__accordion-button",
  level2Wrapper: ".enh-nav__level-2-wrapper",
};

// ─── Class ────────────────────────────────────────────────────────────────────

export class EnhancedNavigationHamburger {
  private el: HTMLElement;
  private abortController: AbortController;

  // Cached elements
  private dialog: HTMLElement | null;
  private dialogContent: HTMLElement | null;
  private serviceNavigation: HTMLElement | null;
  private toggleMobileNavBtns: HTMLElement[];
  private backButtons: HTMLElement[];
  private searchLinks: HTMLAnchorElement[];
  private focusDropdownButtons: HTMLElement[];
  private mainLinks: HTMLAnchorElement[];
  private dialogsLevel2: HTMLElement[];
  private branchesLevel1: HTMLElement[];

  constructor(el: HTMLElement) {
    this.el = el;
    this.abortController = new AbortController();

    this.dialog = el.querySelector<HTMLElement>(SELECTORS.dialog);
    this.dialogContent = el.querySelector<HTMLElement>(SELECTORS.dialogContent);
    this.serviceNavigation = el.querySelector<HTMLElement>(
      SELECTORS.serviceNavigation,
    );
    this.toggleMobileNavBtns = Array.from(
      el.querySelectorAll<HTMLElement>(SELECTORS.toggleMobileNavBtn),
    );
    this.backButtons = Array.from(
      el.querySelectorAll<HTMLElement>(SELECTORS.backButton),
    );
    this.searchLinks = Array.from(
      el.querySelectorAll<HTMLAnchorElement>(SELECTORS.search),
    );
    this.focusDropdownButtons = Array.from(
      el.querySelectorAll<HTMLElement>(SELECTORS.focusDropdownButton),
    );
    // cached within dialog to mirror jQuery's this.$dialog.find(...)
    this.mainLinks = Array.from(
      this.dialog?.querySelectorAll<HTMLAnchorElement>(SELECTORS.lvl1Link) ??
      [],
    );
    this.dialogsLevel2 = Array.from(
      this.dialog?.querySelectorAll<HTMLElement>(SELECTORS.dialogLevel2) ?? [],
    );
    this.branchesLevel1 = Array.from(
      this.dialog?.querySelectorAll<HTMLElement>(SELECTORS.branchLevel1) ?? [],
    );
  }

  init(): void {
    this.handleMobileActiveNavItem();
    this.bindEvents();
    this.openActiveAccordion();
  }

  destroy(): void {
    this.abortController.abort();
  }

  // ─── Event binding ──────────────────────────────────────────────────────────

  private bindEvents(): void {
    const { signal } = this.abortController;

    this.bindToggleNavVisibility(signal);
    this.bindDialogLevel2(signal);
    this.bindBackToLevel1(signal);
    this.bindFocusTraps(signal);
    this.bindEscapePress(signal);
    this.bindAccordionClick(signal);
  }

  // ─── Toggle navigation visibility ────────────────────────────────────────────

  private bindToggleNavVisibility(signal: AbortSignal): void {
    this.toggleMobileNavBtns.forEach((btn) => {
      btn.addEventListener(
        "click",
        () => {
          if (btn.classList.contains(CLASSES.open)) {
            this.closeDialog();
          } else {
            this.openDialog();
          }
        },
        { signal },
      );
    });
  }

  // ─── Open / close dialog ─────────────────────────────────────────────────────

  private openDialog(): void {
    const lvl1ActiveLink = this.el.querySelector<HTMLElement>(
      SELECTORS.lvl1ActiveLink,
    );
    const linkContainer = lvl1ActiveLink?.closest<HTMLElement>(
      SELECTORS.linkContainer,
    );
    const dialogLevel2 = linkContainer
      ? this.getSiblingBySelector(linkContainer, SELECTORS.dialogLevel2)
      : null;

    this.toggleMobileNavBtns.forEach((btn) => {
      btn.classList.add(CLASSES.open);
      btn.setAttribute("aria-expanded", "true");
    });
    this.serviceNavigation?.classList.add(CLASSES.mobileOpen);
    this.dialog?.classList.add(CLASSES.open);
    if (this.dialogContent) this.dialogContent.scrollTop = 0;
    if (this.dialog) this.startAnimation(this.dialog);
    document.body.style.overflow = "hidden";

    if (dialogLevel2) {
      this.openLevel2(dialogLevel2);
    } else {
      if (this.dialogContent) this.dialogContent.style.overflowY = "auto";
    }

    this.closeAllAccordions();
    this.openActiveAccordion();
  }

  private closeDialog(): void {
    this.toggleMobileNavBtns.forEach((btn) => {
      btn.classList.remove(CLASSES.open);
      btn.setAttribute("aria-expanded", "false");
    });
    setTimeout(() => {
      this.dialog?.classList.remove(CLASSES.open);
    }, 400);

    this.dialog?.classList.remove(CLASSES.animationStart);
    if (this.dialogContent) this.dialogContent.style.overflowY = "hidden";
    this.serviceNavigation?.classList.remove(CLASSES.animationStart);
    this.serviceNavigation?.classList.remove(CLASSES.mobileOpen);
    this.serviceNavigation?.classList.remove(CLASSES.mobileLVL2open);
    document.body.style.overflow = "initial";

    setTimeout(() => {
      this.dialog?.classList.remove(CLASSES.mobileLVL2open);
      this.dialogsLevel2.forEach((dropDown) => {
        dropDown.classList.remove(CLASSES.open);
        dropDown.classList.remove(CLASSES.animationStart);
      });
    }, 400);
  }

  // ─── Dialog level-2 ──────────────────────────────────────────────────────────

  private bindDialogLevel2(signal: AbortSignal): void {
    this.mainLinks.forEach((link) => {
      link.addEventListener(
        "click",
        (e: MouseEvent) => {
          e.preventDefault();
          const linkContainer = link.closest<HTMLElement>(SELECTORS.linkContainer);
          const hasDialogLevel2 = linkContainer
            ? !!this.getSiblingBySelector(linkContainer, SELECTORS.dialogLevel2)
            : false;
          if (!hasDialogLevel2) {
            location.href = link.href;
          }
        },
        { signal },
      );

      link.addEventListener(
        "keydown",
        (e: KeyboardEvent) => {
          if (e.code === "Enter" || e.code === "Space") {
            e.preventDefault();
            location.href = link.href;
          }
        },
        { signal },
      );
    });

    this.branchesLevel1.forEach((branch) => {
      branch.addEventListener(
        "click",
        () => {
          const dialogLevel2 = branch.querySelector<HTMLElement>(
            SELECTORS.dialogLevel2,
          );
          if (dialogLevel2 && !dialogLevel2.classList.contains(CLASSES.open)) {
            this.openLevel2(dialogLevel2);
          } else {
            branch.querySelector<HTMLElement>(SELECTORS.lvl1Link)?.click();
          }
        },
        { signal },
      );
    });
  }

  // ─── Open level-2 panel ──────────────────────────────────────────────────────

  private openLevel2(dialogLevel2: HTMLElement): void {
    this.serviceNavigation?.classList.add(CLASSES.mobileLVL2open);
    dialogLevel2.classList.add(CLASSES.open);
    dialogLevel2.scrollTop = 0;
    this.startAnimation(dialogLevel2);
  }

  // ─── Return to level 1 ───────────────────────────────────────────────────────

  private returnToLevel1(): void {
    const openTabs = Array.from(
      this.dialog?.querySelectorAll<HTMLElement>(`.${CLASSES.open}`) ?? [],
    );
    this.serviceNavigation?.classList.remove(CLASSES.mobileLVL2open);
    openTabs.forEach((tab) => tab.classList.remove(CLASSES.animationStart));
    setTimeout(() => {
      openTabs.forEach((tab) => tab.classList.remove(CLASSES.open));
      if (this.dialogContent) this.dialogContent.style.overflowY = "auto";
    }, 200);
  }

  // ─── Back button ─────────────────────────────────────────────────────────────

  private bindBackToLevel1(signal: AbortSignal): void {
    this.backButtons.forEach((btn) => {
      btn.addEventListener(
        "click",
        (e: MouseEvent) => {
          e.preventDefault();
          this.returnToLevel1();
        },
        { signal },
      );

      btn.addEventListener(
        "keydown",
        (e: KeyboardEvent) => {
          if (e.code === "Enter" || e.code === "Space") {
            e.preventDefault();
            this.returnToLevel1();
            const openDialog2 = this.el.querySelector<HTMLElement>(
              SELECTORS.dialogLevel2Open,
            );
            const branchLevel1 = openDialog2?.closest<HTMLElement>(
              SELECTORS.branchLevel1,
            );
            branchLevel1
              ?.querySelector<HTMLElement>(SELECTORS.focusDropdownButton)
              ?.focus();
          }
        },
        { signal },
      );
    });
  }

  // ─── Mobile active item ───────────────────────────────────────────────────────

  private handleMobileActiveNavItem(): void {
    const activeLinks = Array.from(
      this.dialog?.querySelectorAll<HTMLElement>(
        SELECTORS.lvl2and3ActiveLink,
      ) ?? [],
    );
    const last = activeLinks[activeLinks.length - 1];
    last?.classList.add(CLASSES.mobileActive);
  }

  // ─── Focus traps ─────────────────────────────────────────────────────────────

  private bindFocusTraps(signal: AbortSignal): void {
    this.serviceNavigation?.addEventListener(
      "keydown",
      (e: KeyboardEvent) => {
        if (e.code !== "Tab" || !this.isDialogOpen()) return;

        const dialogLevel2 = this.getOpenedLevel2Dialog();
        const backButton = this.backButtons[0];
        const hamburger = this.toggleMobileNavBtns[0];
        const search = this.searchLinks[0];

        if (!e.shiftKey && e.target === hamburger && dialogLevel2) {
          e.preventDefault();
          this.getFocusableElement(dialogLevel2, "first")?.focus();
        } else if (e.shiftKey && e.target === backButton && dialogLevel2) {
          e.preventDefault();
          this.getFocusableElement(dialogLevel2, "last")?.focus();
        } else if (e.shiftKey && e.target === search && !dialogLevel2) {
          const allLinks = Array.from(
            this.el.querySelectorAll<HTMLElement>(
              `${SELECTORS.lvl1Link}, ${SELECTORS.langSelector}`,
            ),
          );
          e.preventDefault();
          allLinks[allLinks.length - 1]?.focus();
        }
      },
      { signal },
    );

    this.dialog?.addEventListener(
      "keydown",
      (e: KeyboardEvent) => {
        if (e.code !== "Tab" || !this.isDialogOpen()) return;

        const dialogLevel2 = this.getOpenedLevel2Dialog();

        if (dialogLevel2) {
          const firstItem = this.getFocusableElement(dialogLevel2, "first");
          const lastItem = this.getFocusableElement(dialogLevel2, "last");

          if (e.shiftKey && firstItem === e.target) {
            e.preventDefault();
            this.focusNavigation("last");
          } else if (!e.shiftKey && lastItem === e.target) {
            e.preventDefault();
            this.focusNavigation("first");
          }
        } else {
          const allLinks = Array.from(
            this.el.querySelectorAll<HTMLElement>(
              `${SELECTORS.lvl1Link}, ${SELECTORS.langSelector}, ${SELECTORS.focusDropdownButton}`,
            ),
          );
          const first = allLinks[0];
          const last = allLinks[allLinks.length - 1];

          if (e.shiftKey && first === e.target) {
            e.preventDefault();
            this.focusNavigation("last");
          } else if (!e.shiftKey && last === e.target) {
            e.preventDefault();
            this.focusNavigation("first");
          }
        }
      },
      { signal },
    );

    this.focusDropdownButtons.forEach((btn) => {
      btn.addEventListener(
        "keydown",
        (e: KeyboardEvent) => {
          if (e.code !== "Enter" && e.code !== "Space") return;
          e.preventDefault();
          const linkContainer = (e.target as HTMLElement).closest<HTMLElement>(
            SELECTORS.linkContainer,
          );
          if (!linkContainer) return;
          const dialogLevel2 = this.getSiblingBySelector(
            linkContainer,
            SELECTORS.dialogLevel2,
          );
          if (dialogLevel2) {
            this.openLevel2(dialogLevel2);
            this.getFocusableElement(dialogLevel2, "first")?.focus();
          }
        },
        { signal },
      );
    });
  }

  // ─── Escape key ──────────────────────────────────────────────────────────────

  private bindEscapePress(signal: AbortSignal): void {
    this.el.addEventListener(
      "keydown",
      (e: KeyboardEvent) => {
        if (e.code === "Escape" && this.isDialogOpen()) {
          this.closeDialog();
          this.searchLinks[0]?.focus();
        }
      },
      { signal },
    );
  }

  // ─── Accordion ───────────────────────────────────────────────────────────────

  private bindAccordionClick(signal: AbortSignal): void {
    this.el
      .querySelectorAll<HTMLElement>(SELECTORS.accordionButton)
      .forEach((btn) => {
        btn.addEventListener(
          "click",
          () => {
            const isOpened = btn.classList.contains(CLASSES.accordionOpened);
            this.closeAllAccordions();
            this.toggleAccordion(btn, !isOpened);
          },
          { signal },
        );
      });
  }

  private closeAllAccordions(): void {
    this.el
      .querySelectorAll<HTMLElement>(SELECTORS.accordionButton)
      .forEach((btn) => {
        this.toggleAccordion(btn, false);
      });
  }

  private openActiveAccordion(): void {
    const activeLinks = this.el.querySelectorAll<HTMLElement>(
      SELECTORS.lvl2and3ActiveLink,
    );
    const activeLink = activeLinks[0];
    if (!activeLink) return;
    const level2Wrapper = activeLink.closest<HTMLElement>(
      SELECTORS.level2Wrapper,
    );
    const accordionButton = level2Wrapper?.querySelector<HTMLElement>(
      SELECTORS.accordionButton,
    );
    if (accordionButton) this.toggleAccordion(accordionButton, true);
  }

  private toggleAccordion(element: HTMLElement, open: boolean): void {
    element.classList.toggle(CLASSES.accordionOpened, open);
    element.setAttribute("aria-expanded", String(open));
  }

  // ─── Animation helper ────────────────────────────────────────────────────────

  private startAnimation(elem: HTMLElement, delay: number = 1): void {
    setTimeout(() => {
      elem.classList.add(CLASSES.animationStart);
    }, delay);
  }

  // ─── Focus helpers ────────────────────────────────────────────────────────────

  private focusNavigation(order: "first" | "last"): void {
    if (order === "last") {
      this.toggleMobileNavBtns[0]?.focus();
    } else {
      const dialogLevel2 = this.getOpenedLevel2Dialog();
      if (dialogLevel2) {
        this.backButtons[0]?.focus();
      } else {
        this.searchLinks[0]?.focus();
      }
    }
  }

  private getOpenedLevel2Dialog(): HTMLElement | null {
    return this.el.querySelector<HTMLElement>(SELECTORS.dialogLevel2Open);
  }

  private isDialogOpen(): boolean {
    return this.dialog?.classList.contains(CLASSES.open) ?? false;
  }

  private getFocusableElement(
    root: HTMLElement,
    order: "first" | "last",
  ): HTMLElement | null {
    const elements = Array.from(
      root.querySelectorAll<HTMLElement>(SELECTORS.focusableElements),
    );
    if (order === "first") return elements[0] ?? null;
    return elements[elements.length - 1] ?? null;
  }

  // ─── DOM sibling helper ───────────────────────────────────────────────────────

  private getSiblingBySelector(
    el: Element,
    selector: string,
  ): HTMLElement | null {
    return (
      (Array.from(el.parentElement?.children ?? []).find(
        (child) => child !== el && child.matches(selector),
      ) as HTMLElement | null) ?? null
    );
  }

  // ─── Static bootstrap ───────────────────────────────────────────────────────

  static initAll(root: Document | HTMLElement = document): void {
    root
      .querySelectorAll<HTMLElement>(
        '[data-component="enhanced-navigation-hamburger"]',
      )
      .forEach((el) => {
        new EnhancedNavigationHamburger(el).init();
      });
  }
}
